import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BellRing, ChevronRight, Info, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import AdSlot from '../components/advertisement/AdSlot';
import FaqSection from '../components/common/FaqSection';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import DrhpJourneySection from '../components/upcoming/DrhpJourneySection';
import ResearchReportsCard from '../components/upcoming/ResearchReportsCard';
import OpeningSoonSection from '../components/upcoming/OpeningSoonSection';
import { SME_JOURNEY_STEPS, SME_UPCOMING_FAQS } from '../data/upcomingIposData';
import { formatMonthLabel, monthKeyOf, todayIso } from '../utils/ipoCalendar';

const NOTICE_MS = 4500;
const HEADING = 'Upcoming SME IPOs: NSE Emerge & BSE SME';

const JOURNEY_INTRO =
  'Before an SME IPO opens for bidding, its draft offer document goes through the SME platform of the stock exchange, which vets it in place of SEBI.';

const JOURNEY_NOTE = (
  <>
    <strong className="font-bold text-violet-900">Exchange-vetted documents:</strong> because SME offer documents are
    reviewed by the exchange rather than SEBI, read the RHP closely — promoter background, related-party dealings, cash
    flows and how the issue proceeds will be used.
  </>
);

// const DISCUSSION_COPY = {
//   headingId: 'sme-upcoming-discussion-heading',
//   title: 'SME IPO Discussion & Query Hub',
//   subtitle: 'Discuss upcoming SME issues, lot sizes, exchange approvals and grey market expectations.',
//   placeholder: 'Write a comment or ask about an upcoming SME issue…',
//   submitLabel: 'Post a Query',
// };

export const SmeUpcomingIposPage = () => {
  const today = useMemo(() => todayIso(), []);
  const [notice, setNotice] = useState(null);

  // OpeningSoonSection loads GET /api/market/ipos/sme/upcoming; its refresh time dates the page
  const refreshedAt = useSelector((state) => state.smeUpcomingIpos.refreshedAt);
  const lastUpdated = refreshedAt ? formatMonthLabel(monthKeyOf(refreshedAt.slice(0, 10))) : null;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADING} | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pb-12 sm:pb-16 space-y-6 sm:space-y-7">
          {/* Sponsored banner (GET /api/ads): dedicated SME slot first, then the home leaderboard */}
          <AdSlot slotId={['sme-upcoming-ipos-top', 'leaderboard']} />

          {/* Breadcrumb + header */}
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/sme-ipos" className="hover:text-[#008080] transition-colors">
                SME IPOs
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-[#008080] font-semibold">Upcoming SME IPOs</span>
            </nav>

            <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {lastUpdated ? `Updated ${lastUpdated} Schedule` : 'NSE Emerge & BSE SME Schedule'}
                </span>
                <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">{HEADING}</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  SME issues scheduled to open for bidding on <strong className="text-slate-700">NSE Emerge &amp; BSE SME</strong>.
                  Track bidding dates, price band, lot size and the per-lot amount you need, plus how an SME offer document
                  moves from exchange filing to listing.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <Link
                  to="/sme-ipos"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 shadow-sm transition-colors"
                >
                  All SME IPOs
                </Link>
                {/* <button
                  type="button"
                  onClick={() => setNotice('SME IPO alerts are coming soon. New SME issues appear on this page as soon as their dates are announced.')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white shadow-sm transition-colors"
                >
                  <BellRing className="w-3.5 h-3.5" />
                  Set SME Alerts
                </button> */}
              </div>
            </div>
          </div>

          {/* SME issues with confirmed bidding dates (GET /api/market/ipos/sme/upcoming) */}
          <OpeningSoonSection
            today={today}
            feed="sme"
            lockedBoard="sme"
            title="SME IPOs Opening Soon"
            subtitle="SME issues scheduled to open for bidding, with price band, lot size, per-lot value and issue size."
          />

          <DrhpJourneySection
            title="Understanding the SME IPO Approval Journey"
            intro={JOURNEY_INTRO}
            steps={SME_JOURNEY_STEPS}
            note={JOURNEY_NOTE}
          />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 items-start">
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <FaqSection
                id="sme-upcoming-faq"
                eyebrow="Help Center"
                title="Frequently Asked Questions on Upcoming SME IPOs"
                faqs={SME_UPCOMING_FAQS}
              />
              {/* Discussion section hidden for now: <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} /> */}

            </div>
            <aside className="space-y-5 sm:space-y-6" aria-label="Partners and reports">
              {/* Partner ad (GET /api/ads): dedicated SME slot first, then the in-feed poster */}
              <AdSlot slotId={['sme-upcoming-ipos-sidebar', 'infeed']} variant="compact" />
              <ResearchReportsCard
                title="SME IPO Reviews"
                subtitle="Broker calls & research notes for SME issues"
                body="See what brokerages and independent analysts say about upcoming and recent SME issues, with links to the original notes."
                linkLabel="Browse SME Reviews"
                to="/sme-ipo-reviews"
              />
            </aside>
          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default SmeUpcomingIposPage;
